// Scene-graph cost of a map view (§12.9 budgets): draw calls and triangles counted from the
// visible objects, conservatively (no frustum culling), including the shadow pass for casters
// when shadows are on. Used by the budget unit test and the `?debug=maps` page.
import type { BufferGeometry, InstancedMesh, Mesh, Object3D } from 'three';

/** The WP6 share of the §12.9 scene budgets: draw calls and triangles per tier. */
export const MAP_BUDGET = {
  high: { drawCalls: 60, triangles: 160_000 },
  medium: { drawCalls: 48, triangles: 100_000 },
  low: { drawCalls: 34, triangles: 60_000 },
  potato: { drawCalls: 28, triangles: 50_000 },
} as const;

function triangles(geo: BufferGeometry): number {
  const n = geo.index ? geo.index.count : (geo.getAttribute('position')?.count ?? 0);
  const range = Math.min(n, geo.drawRange.count === Infinity ? n : geo.drawRange.count);
  return Math.floor(range / 3);
}

export function measureBudget(root: Object3D, shadows: boolean): { drawCalls: number; triangles: number; shadowCasters: number } {
  let drawCalls = 0;
  let tris = 0;
  let shadowCasters = 0;
  const visit = (o: Object3D): void => {
    if (!o.visible) return;
    const mesh = o as Mesh;
    if (mesh.isMesh) {
      const inst = (o as InstancedMesh).isInstancedMesh ? (o as InstancedMesh).count : 1;
      if (inst > 0) {
        const groups = Array.isArray(mesh.material) ? Math.max(1, mesh.geometry.groups.length) : 1;
        drawCalls += groups;
        const t = triangles(mesh.geometry) * inst;
        tris += t;
        if (shadows && mesh.castShadow) {
          drawCalls += groups;
          tris += t;
          shadowCasters++;
        }
      }
    } else if ((o as { isLine?: boolean }).isLine || (o as { isPoints?: boolean }).isPoints) {
      drawCalls++;
    }
    for (const c of o.children) visit(c);
  };
  visit(root);
  return { drawCalls, triangles: tris, shadowCasters };
}
